/**
 * CSU East Bay brand colors.
 *
 * Source: Cal State East Bay Brand Identity Guide, "Type Fonts and Colors"
 * (https://www.csueastbay.edu/universitycommunications/brand/fonts-and-colors.html,
 * fetched 2026-10-30, W56 S0): "Pantone Matching System (PMS) 199 red and solid
 * black". The primary swatch image gives the hex values (onboarding probes
 * www_brand_primary.jpg / www_brand_secondary.jpg):
 *
 *   Primary:   Red #D50032 (rgb 213-0-50)   Black #000000   Gold #FDC25A
 *   Secondary: Teal #00A0AD   Purple #7A236A   Orange #F6901E
 *
 * #D50032 also appears inline in the catalog's own landing page (a second
 * first-party source, doc 51 S0-7).
 *
 * Contrast (WCAG, measured): Red #D50032 on white 5.42:1, so it passes AA for
 * white text and stays the primary DEFAULT. `-strong` is the ramp's 700 step,
 * #A60027 (7.92:1 with white), for solid fills; East Bay publishes no dark red,
 * so this tier is DERIVED, not a brand value.
 *
 * Accent (PROPOSED to the W56 driver, same logic as SDSU's): no published brand
 * colour clears 4.5:1 on BOTH the white surface and the warm charcoal #161412 —
 * black 21.0 / 1.14, gold 1.61 / 11.41, teal #00A0AD 3.17 / 5.80, purple 9.23 /
 * 1.99, orange 2.35 / 7.81 (white / dark). The accent is the brand teal darkened
 * to #007F8A: 4.77:1 on white (AA), 3.85:1 on dark (AA-large) — the best
 * two-surface choice, and DERIVED from the brand teal rather than published.
 *
 * Brand mark: the cross-theme distance floor was removed by owner decision
 * (2026-10-14, "a school's mark follows its own brand"), so East Bay's mark uses
 * its own red even though SDSU's Bright Red (#D41736) is close to it.
 *
 * The 50-900 ladders are GENERATED around the brand values (tints mix with
 * white, shades with black, in the step proportions of the SDSU ladder) — brand
 * aligned, not officially endorsed. Logo policy mirrors UMD/SJSU/SDSU: text-only
 * branding, no Pioneer/wordmark asset embedded.
 */
import type { ThemeColors } from '../types';

export const csueastbayColors: ThemeColors = {
  // Primary — East Bay Red (#D50032 = rgb(213, 0, 50))
  '--color-primary':     '213 0 50',
  // -strong: the 700 step (#A60027, 7.92:1 with white) for white-text fills.
  '--color-primary-strong': '166 0 39',
  '--color-primary-50':  '253 242 245',
  '--color-primary-100': '250 227 232',
  '--color-primary-200': '245 196 208',
  '--color-primary-300': '237 148 169',
  '--color-primary-400': '227 84 118',
  '--color-primary-500': '213 0 50',
  '--color-primary-600': '190 0 44',
  '--color-primary-700': '166 0 39',
  '--color-primary-800': '134 0 32',
  '--color-primary-900': '104 0 24',

  // Accent — brand teal darkened to #007F8A (rgb(0, 127, 138)); see header.
  '--color-accent':     '0 127 138',
  '--color-accent-50':  '242 249 249',
  '--color-accent-100': '227 241 242',
  '--color-accent-200': '196 226 228',
  '--color-accent-300': '148 201 206',
  '--color-accent-400': '84 169 177',
  '--color-accent-500': '0 127 138',
  '--color-accent-600': '0 113 123',
  '--color-accent-700': '0 99 108',
  '--color-accent-800': '0 80 87',
  '--color-accent-900': '0 62 68',

  // Brand-mark faces (components/ui/BrandMark.tsx). Front = the brand red in
  // both appearances; the oblique faces step down the ramp (700 / 800 light,
  // 600 / 700 dark).
  '--color-mark-front': '213 0 50',
  '--color-mark-top':   '166 0 39',
  '--color-mark-side':  '134 0 32',
  '--color-mark-front-dm': '213 0 50',
  '--color-mark-top-dm':   '190 0 44',
  '--color-mark-side-dm':  '166 0 39',
};
